import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from 'react-oidc-context';
import ChatBot from './ChatBot';
import LoginPage from './components/LoginPage';
import TeamsConfigPage from './components/TeamsConfigPage';
import TeamsIntegration, { TeamsContextInfo } from './components/TeamsIntegration';

import { TeamsAuthCallbackPage, TeamsAuthStartPage } from './components/TeamsAuthPages';
import { isDevModeLocalOverrideEnabled } from './utils/devMode';
import { TEAMS_AUTH_IN_PROGRESS_KEY } from './utils/teamsAuthFlow';
import AdminGrantsApp from './admin/AdminGrantsApp';
import { useSessionRenewal } from './hooks/useSessionRenewal';

/** How long a Teams SSO attempt stays in-progress in sessionStorage. */
const TEAMS_AUTH_TIMEOUT_MS = 10 * 60 * 1000;

const hasRecentTeamsAuthAttempt = () => {
  const startedAt = Number(sessionStorage.getItem(TEAMS_AUTH_IN_PROGRESS_KEY) || 0);
  return startedAt > 0 && Date.now() - startedAt < TEAMS_AUTH_TIMEOUT_MS;
};

const App: React.FC = () => {
  const { isAuthenticated, isLoading, user } = useAuth();
  useSessionRenewal();

  // Identify the logged-in user in Microsoft Clarity by email
  useEffect(() => {
    if (!isAuthenticated) return;
    const token = user?.access_token;
    if (!token) {
      return;
    }

    const fetchAndSetEmail = async () => {
      try {
        const res = await fetch('https://auth.connectwise.com/connect/userinfo', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const info = await res.json() as Record<string, unknown>;
        const email = (info['email'] ?? info['preferred_username']) as string | undefined;
        if (email) {
          window.clarity?.('identify', email);
          window.clarity?.('set', 'email', email);
        }
      } catch (error) {
        console.error('Error ocuured while identifying user in Clarity', error);
      }
    };
    void fetchAndSetEmail();
  }, [isAuthenticated, user?.access_token]);

  const [teamsContext, setTeamsContext] = useState<TeamsContextInfo | null>(null);
  const [currentRoute, setCurrentRoute] = useState<string>('');
  const [, setIsAuthenticated] = useState(isAuthenticated);

  // Determine current route based on URL parameters and pathname
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const route = urlParams.get('route') || '';
    setCurrentRoute(route);

    const fakeUser = localStorage.getItem('oidc.user:<authority>:<client_id>');
    if (fakeUser) {
      setIsAuthenticated(true);
    }

    // Debug logging
    console.log('App.tsx Debug:', {
      pathname: window.location.pathname,
      search: window.location.search,
      route: route,
      href: window.location.href
    });
  }, []);

  // Handle Teams context updates
  const handleTeamsContext = useCallback((context: TeamsContextInfo) => {
    console.log('Teams context received:', context);
    setTeamsContext(context);
  }, []);

  // Teams configuration page - check pathname first, then query param
  const isTeamsConfig = window.location.pathname === '/teams-config' ||
    window.location.pathname === '/teams-config/' ||
    currentRoute === 'teams-config';

  console.log('Teams config check:', {
    pathname: window.location.pathname,
    currentRoute,
    isTeamsConfig,
    isAuthenticated
  });

  const isAdminRoute =
    window.location.pathname === '/admin' || window.location.pathname === '/admin/';
  const devModeEnabled = isDevModeLocalOverrideEnabled();

  let page: React.ReactNode;

  if (isTeamsConfig) {
    page = (
      <TeamsIntegration onTeamsContext={handleTeamsContext}>
        <TeamsConfigPage />
      </TeamsIntegration>
    );
  } else if (window.location.pathname === '/teams-auth-start') {
    page = <TeamsAuthStartPage />;
  } else if (window.location.pathname === '/callback' && hasRecentTeamsAuthAttempt()) {
    page = <TeamsAuthCallbackPage />;
  } else if (window.location.pathname === '/callback') {
    page = (
      <div className="min-h-screen bg-slate-900 text-white flex items-center justify-center p-6">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-cyan-400 mx-auto mb-4"></div>
          <p className="text-sm text-slate-200">Completing sign-in…</p>
        </div>
      </div>
    );
  } else if (isLoading && !devModeEnabled) {
    page = (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-indigo-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-white text-lg">Loading...</p>
        </div>
      </div>
    );
  } else if (!isAuthenticated && !devModeEnabled) {
    page = (
      <TeamsIntegration onTeamsContext={handleTeamsContext}>
        <div className={teamsContext?.isInTeams ? 'teams-embedded' : 'standalone-app'}>
          <LoginPage teamsHostConfirmed={teamsContext?.isInTeams === true} />
        </div>
      </TeamsIntegration>
    );
  } else if (isAdminRoute) {
    page = (
      <TeamsIntegration onTeamsContext={handleTeamsContext}>
        <div className={teamsContext?.isInTeams ? 'teams-embedded' : 'standalone-app'}>
          <AdminGrantsApp />
        </div>
      </TeamsIntegration>
    );
  } else {
    page = (
      <TeamsIntegration onTeamsContext={handleTeamsContext}>
        <div className={teamsContext?.isInTeams ? 'teams-embedded' : 'standalone-app'}>
          <ChatBot />
        </div>
      </TeamsIntegration>
    );
  }

  return <>{page}</>;
};

export default App;
